// components/sections/Hero.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Container from '../common/Container';
import Button from '../common/Button';
import { isDownloadReady, openDownload } from '../../config/download';

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: var(--background-primary);
  padding-top: 70px;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: -50px;
    right: -150px;
    width: 600px;
    height: 500px;
    border-radius: 70% 50% 60% 40%;
    background: radial-gradient(ellipse, rgba(255,255,255,1) 0%, var(--accent-primary) 25%, var(--accent-secondary) 55%, var(--accent-tertiary) 100%);
    opacity: 0.7;
    z-index: 1;
    box-shadow: 0 0 60px 30px rgba(240, 180, 50, 0.6);
    animation: shine 2s infinite alternate;
    transform: rotate(-15deg);

    @media (max-width: 1200px) {
      width: 500px;
      height: 400px;
      right: -100px;
    }

    @media (max-width: 768px) {
      width: 300px;
      height: 250px;
      top: -30px;
      right: -50px;
      box-shadow: 0 0 30px 15px rgba(240, 180, 50, 0.6);
    }

    @media (max-width: 480px) {
      width: 200px;
      height: 180px;
      top: -20px;
      right: -30px;
      box-shadow: 0 0 20px 10px rgba(240, 180, 50, 0.6);
    }
  }

  @keyframes shine {
    from {
      box-shadow: 0 0 60px 30px rgba(240, 180, 50, 0.6);
      filter: brightness(1);
    }
    to {
      box-shadow: 0 0 90px 45px rgba(240, 180, 50, 0.8);
      filter: brightness(1.2);
    }
  }
`;

const HeroContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4rem;

  @media (max-width: 968px) {
    flex-direction: column-reverse;
    text-align: center;
  }
`;

const HeroText = styled.div`
  flex: 1;
`;

const HeroImage = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;

  img {
    width: 100%;
    max-width: 600px;
    height: auto;
  }

  .logo {
    margin-bottom: 1rem;
    max-width: 200px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: var(--text-primary);

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  color: var(--text-secondary);
  margin-bottom: 2rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-start;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const Hero = () => {
    const navigate = useNavigate();

    return (
        <HeroSection id="home">
            <Container>
                <HeroContent>
                    <HeroText>
                        <HeroTitle>Sivo: The Ultimate Tool for Sign Manufacturers</HeroTitle>
                        <HeroSubtitle>
                            Signage proudly presents Sivo - designed specifically for sign manufacturers
                            to streamline operations, reduce costs, and maximize profits.
                        </HeroSubtitle>
                        <ButtonContainer>
                            <Button variant="primary" onClick={() => navigate('/get-started?mode=register')}>Get Started</Button>
                            <Button
                                variant="secondary"
                                disabled={!isDownloadReady()}
                                onClick={openDownload}
                                title={isDownloadReady() ? 'Download the Sivo desktop app' : 'Desktop app download coming soon'}
                            >
                                {isDownloadReady() ? 'Download App' : 'Download (Coming Soon)'}
                            </Button>
                        </ButtonContainer>
                    </HeroText>
                    <HeroImage>
                        {/* Right side intentionally left empty */}
                    </HeroImage>
                </HeroContent>
            </Container>
        </HeroSection>
    );
};

export default Hero;
